import React from "react";

const experiences = [
  {
    role: "AI Integration & Full-Stack Developer (Intern → Full-Time → Contract)",
    org: "CarbonOne",
    location: "Toronto, ON",
    dates: "September 2025 – Present",
    bullets: [
      "Developed and maintained scalable full-stack features for a carbon accounting platform, leveraging React, Node.js, TypeScript, AWS, and CI/CD pipelines to deliver reliable production-ready solutions.",
      "Led AWS infrastructure hardening and optimization initiatives across EC2, RDS, ALB, IAM, and VPC environments; implemented SCP-based access controls, analyzed CloudTrail activity and GuardDuty findings, and remediated security misconfigurations, reducing cloud spend by 15% while improving operational security and compliance.",
      "Architected and deployed AI-driven automation workflows and API integrations using n8n, partnering with Product and Science teams to streamline business operations, reduce manual processes, and support data-informed decision-making.",
      "Optimized website performance and SEO, improving load speed, search visibility, and user experience.",
    ],
    tech:["TypeScript", "React", "Node.js", "AWS", "GCP", "n8n", "REST APIs", "Webflow", "GitHub"]
  },
  {
    role: "Research Assistant – Geospatial Artificial Intelligence",
    org: "School of the Environment, University of Windsor",
    location: "Windsor, ON",
    dates: "September 2025 – Present",
    bullets: [
      "Engineered an end-to-end geospatial data pipeline to preprocess 40 years (1979–2018) of GlobSnow Snow Water Equivalent (SWE) satellite data using GDAL, ArcGIS Pro, Pandas, and NumPy, supporting large-scale climate trend analysis across North America.",
      "Developed and trained a TensorFlow-based Siamese U-Net architecture with SSIM-guided feature learning and contrastive loss to detect daily changes in Snow Water Equivalent from passive microwave remote-sensing imagery.",
      "Designed automated preprocessing workflows including reprojection, spatial subsetting, normalization, and generation of 72×72 geospatial raster tiles, improving training efficiency and data consistency across multiple climatological regions.",
      "Evaluated model performance using ROC-AUC, Precision, Recall, and F1-score metrics, achieving up to 99.3% F1-score on independent validation datasets and demonstrating strong geographic generalization across previously unseen regions.",
      {
        text: "Publication: ",
        linkText:
          "Daily Snow-Water-Equivalent Trends over the Great Lakes Basin: A Computer Vision and Deep Learning-Based Approach",
        href: "https://www.mdpi.com/2225-1154/14/5/95"
      }
    ],
    tech: [ "Python", "TensorFlow", "GDAL", "CNNs", "Siamese U-Net", "Pandas", "NumPy", "Geospatial Climate Data"]
  },
  {
    role: "Research Assistant — Latent Aspect Detection (LADy)",
    org: "School of Computer Science, University of Windsor",
    location: "Windsor, ON",
    dates: "Apr 2025 – September 2025",
    bullets: [
      "Built ABSA pipeline: data ingestion (SemEval/Google Reviews), preprocessing, and evaluation (precision/recall/F1).",
      "Prototyped LLM prompting with JSON-safe outputs; added guardrails + error recovery.",
      "Served models via FastAPI with health/metrics endpoints for batch evaluations."
    ],
    tech: ["Python", "PyTorch", "LLMs", "FastAPI", "Pandas"]
  },
  {
    role: "Artificial Intelligence Intern",
    org: "Glendor Inc.",
    location: "Draper, UT (Remote)",
    dates: "Jan 2025 – Apr 2025",
    bullets: [
      "Shipped NLP utilities for entity/intent extraction used in internal workflows.",
      "Optimized inference jobs and added caching to cut latency on common queries.",
      "Delivered reproducible notebooks and dashboards for model analysis."
    ],
    tech: ["Python", "scikit-learn", "spaCy", "Pandas"]
  },
  {
    role: "Teaching Assistant",
    org: "School of Computer Science, University of Windsor",
    location: "Windsor, ON",
    dates: "Sep 2024 – Apr 2025",
    bullets: [
      "Led weekly labs; clarified DS/Algo concepts and graded assignments with detailed feedback.",
      "Created starter code and unit tests to standardize grading and reduce errors."
    ],
    tech: ["C - Programming Language"]
  }
];

const Chip = ({ children }) => (
  <span className="rounded-md border border-gray-700 px-2 py-0.5 text-xs text-gray-300">
    {children}
  </span>
);

const Bullet = ({ bullet }) => {
  if (typeof bullet === "string") return bullet;

  return (
    <>
      {bullet.text}
      <a
        href={bullet.href}
        target="_blank"
        rel="noreferrer"
        className="text-blue-400 underline underline-offset-2 hover:text-blue-300"
      >
        {bullet.linkText}
      </a>
    </>
  );
};

const Experience = () => {
  return (
    <section id="experience" className="bg-gray-910 text-gray-100 py-16">
      <div className="mx-auto max-w-5xl px-4">
        <h2 className="text-4xl font-bold text-white text-center">My Experience</h2>
        <p className="mt-3 text-center text-gray-400">
          Highlights from research, internships, and teaching.
        </p>

        {/* Timeline */}
        <ol className="mt-10 relative border-l border-gray-700">
          {experiences.map((exp, i) => (
            <li key={i} className="mb-10 ml-4">
              {/* Dot */}
              <span className="absolute -left-2.5 flex h-5 w-5 items-center justify-center rounded-full bg-blue-600 ring-4 ring-gray-900" />

              {/* Card */}
              <div className="rounded-xl border border-gray-800 bg-gray-850/50 p-5 shadow">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <h3 className="text-xl font-semibold text-white">{exp.role}</h3>
                  <span className="text-sm text-gray-400">{exp.dates}</span>
                </div>
                <p className="mt-1 text-gray-300">
                  {exp.org} • {exp.location}
                </p>

                <ul className="mt-3 list-disc space-y-2 pl-5 text-sm text-gray-300">
                  {exp.bullets.map((b, j) => (
                    <li key={j}>
                      <Bullet bullet={b} />
                    </li>
                  ))}
                </ul>

                <div className="mt-3 flex flex-wrap gap-2">
                  {exp.tech.map((t) => (
                    <Chip key={t}>{t}</Chip>
                  ))}
                </div>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
};

export default Experience;
